



import axios from "axios";
import html2pdf from "html2pdf.js";
import { getDocument } from 'pdfjs-dist';
import QRCode from 'qrcode';
import Sanscript from 'sanscript';

// Helper function to convert Base64 PDF to Image
export const convertBase64PdfToImage = async(base64Data) => {
    try {
        const imageSignatures = {
            '/9j/': 'jpeg',
            'iVBORw0KGgo': 'png',
            'R0lGODlh': 'gif',
        };

        for (const [signature, format] of Object.entries(imageSignatures)) {
            if (base64Data.startsWith(signature)) {
                console.log(`Detected ${format.toUpperCase()} format, returning as image directly`);
                return `data:image/${format};base64,${base64Data}`;
            }
        }

        console.log('Processing as PDF...');

        const cleanBase64 = base64Data.replace(/^data:.*?;base64,/, '');
        const pdfData = atob(cleanBase64);
        const uint8Array = new Uint8Array([...pdfData].map(char => char.charCodeAt(0)));

        const loadingTask = getDocument({ data: uint8Array });
        const pdf = await loadingTask.promise;
        const page = await pdf.getPage(1);

        const scale = 2;
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement("canvas");
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        const context = canvas.getContext("2d");
        await page.render({ canvasContext: context, viewport }).promise;

        return canvas.toDataURL("image/png");

    } catch (error) {
        console.error("Error processing base64 data:", error);
        console.log("Falling back to direct image usage");

        const formats = ['jpeg', 'png', 'jpg'];
        for (const format of formats) {
            try {
                const testImg = new Image();
                const dataUrl = `data:image/${format};base64,${base64Data}`;

                return new Promise((resolve, reject) => {
                    testImg.onload = () => resolve(dataUrl);
                    testImg.onerror = () => reject();
                    testImg.src = dataUrl;
                });
            } catch (e) {
                continue;
            }
        }

        throw new Error("Unable to process base64 data as either PDF or image");
    }
};

// Helper function to convert gender to English with Hindi translation
export const getGenderInEnglish = (gender) => {
    if (!gender) return 'N/A';
    const genderMap = {
        'Male': 'పురుషుడు',
        'Female': 'స్త్రీ',
        'Other': 'ఇతర',
        'M': 'పురుషుడు',
        'F': 'స్త్రీ'
    };
    return genderMap[gender] || gender;
};

// Helper function to generate QR Code
export const generateQRCodeBase64 = async(patient) => {
    const patientInfo = `Patient ID: ${patient.unique_patient_id}
Name: ${patient.name}
Age: ${patient.age}
Loan Number: ${patient.loan_number}
Adahar Number: ${patient.aadhar_number}
Phone: ${patient.phone}
Gender: ${patient.gender}`;

    try {
        console.log("Generating QR Code for:", patientInfo);

        if (typeof QRCode === 'undefined') {
            throw new Error("QRCode library not loaded");
        }

        const qrCode = await QRCode.toDataURL(patientInfo, {
            width: 120,
            margin: 2,
            color: {
                dark: '#000000',
                light: '#FFFFFF'
            }
        });

        console.log("QR Code generated successfully");
        return qrCode;
    } catch (err) {
        console.error("QR generation error:", err);
        throw err;
    }
};

// Main function to handle smart report generation
export const handletelaguSmartReportGenerate = async(patient, baseUrl, apiBaseUrl) => {
    try {
        // 1. Get full smart report data
        const apiUrl = `${apiBaseUrl}/api/technician/smart-report/${patient.unique_patient_id}/`;
        console.log("📡 Fetching smart report from:", apiUrl);

        const { data } = await axios.get(apiUrl);
        const { patient: p, vitals, bmr_pdf_base64 } = data;
        console.log("✅ Smart Report Data received:", data);

        let bmrData = {};
        try {
            const bmrResponse = await axios.get(`${apiBaseUrl}/api/technician/medibuddy/bmr/${patient.unique_patient_id}/`);
            bmrData = bmrResponse.data?.data || {};
            console.log("✅ BMR Data received:", bmrData);
        } catch (bmrErr) {
            console.warn("⚠️ BMR data fetch failed, using empty data:", bmrErr.message);
        }

        // 2. Create temporary div for 3-page PDF
        const container = document.createElement("div");

        // Helper function to determine if value is in normal range
        const getValueStatus = (value, min, max) => {
            if (value === null || value === undefined || value === '') return '';
            const numValue = parseFloat(value);
            if (numValue < min) return 'Low';
            if (numValue > max) return 'High';
            return 'Normal';
        };



        const isInvalid = (value) => {
            return (
                value === null ||
                value === undefined ||
                value === '' ||
                Number(value) === 0
            );
        };

        // Helper function to safely get and return values
        const getValue = (value, defaultValue = 'N/A') => {
            return isInvalid(value) ? defaultValue : value;
        };


        // Helper function to get decimal value with specified places
        const getDecimalValue = (value, decimalPlaces = 2, defaultValue = 'N/A') => {
            if (isInvalid(value)) return defaultValue;
        
            const numValue = parseFloat(value);
            if (isNaN(numValue)) return defaultValue;
        
            return numValue.toFixed(decimalPlaces);
        };

        // Helper function to get vital status and colors
        const getVitalStatus = (type, value, gender = 'Male') => {
            const num = parseFloat(value);
            const GREEN  = '#00C47A';
            const ORANGE = '#FB8C00';
            const RED    = '#E53935';
            const makeBar = (color) => {
                const zones = {
                    [GREEN]:  `<div style="background:${GREEN};flex:5"></div>`,
                    [ORANGE]: `<div style="background:${RED};flex:1"></div><div style="background:${ORANGE};flex:3.5"></div><div style="background:${RED};flex:0.5"></div>`,
                    [RED]:    `<div style="background:${RED};flex:5"></div>`,
                };
                return zones[color] || zones[GREEN];
            };
            const defaultBar = () => makeBar(GREEN);
            if (isNaN(num)) return { status: 'unknown', valueColor: '#1a1a1a', bar: defaultBar() };

            let color = GREEN;

            switch (type) {
                case 'temperature':
                    if (num <= 89.6 || num >= 104) color = RED;
                    else if (num < 97.7 || num > 99.5) color = ORANGE;
                    break;
                case 'pulse':
                    if (num <= 40 || num >= 130) color = RED;
                    else if (num < 60 || num > 100) color = ORANGE;
                    break;
                case 'spo2':
                    if (num <= 85) color = RED;
                    else if (num <= 90) color = ORANGE;
                    break;
                case 'bp_systolic':
                    if (num <= 80 || num >= 180) color = RED;
                    else if (num < 90 || num > 120) color = ORANGE;
                    break;
                case 'bp_diastolic':
                    if (num <= 50 || num >= 120) color = RED;
                    else if (num < 60 || num > 80) color = ORANGE;
                    break;
                case 'glucose':
                    if (num >= 126) color = RED;
                    else if (num >= 100) color = ORANGE;
                    else if (num < 70) color = RED;
                    break;
                case 'hemoglobin': {
                    const lo = gender === 'Female' ? 12 : 13;
                    const hi = gender === 'Female' ? 15 : 17;
                    if (num <= 7 || num >= 20) color = RED;
                    else if (num < lo || num > hi) color = ORANGE;
                    break;
                }
                default: break;
            }

            return { color, bar: makeBar(color) };
        };

        // Helper function to format BP string into systolic and diastolic
        const formatBP = (bpString) => {
            if (!bpString) return { systolic: 'N/A', diastolic: 'N/A' };
            const parts = bpString.toString().split('/');
            return {
                systolic: getValue(parts[0]?.trim(), 'N/A'),
                diastolic: getValue(parts[1]?.trim(), 'N/A')
            };
        };
        
        // Helper function to calculate BMI
        const calculateBMI = (vitals, patient) => {
            const height = parseFloat(vitals?.height) || 0;
            const weight = parseFloat(vitals?.weight) || 0;
            if (height === 0 || weight === 0) return 'N/A';
            const heightInMeters = height / 100;
            const bmi = (weight / (heightInMeters * heightInMeters)).toFixed(2);
            return isNaN(bmi) ? 'N/A' : bmi;
        };
        
        
                // Helper function to check if value is out of range
          const isOutOfRange = (type, value, gender = 'Male') => {
              const num = parseFloat(value);
              if (isNaN(num) || !value) return false;
  
              switch (type) {
                  case 'temperature':
                      return num < 97.7 || num > 99.5;        // Normal: 97.7–99.5
                  case 'pulse':
                      return num < 60 || num > 100;            // Normal: 60–100
                  case 'spo2':
                      return num < 95;                         // Normal: ≥95
                  case 'bp_systolic':
                      return num < 90 || num > 120;            // Normal: 90–120
                  case 'bp_diastolic':
                      return num < 60 || num > 80;             // Normal: 60–80
                  case 'glucose':
                      return num < 70 || num >= 100;           // Normal: 70–99
                  case 'hemoglobin': {
                      const lo = gender === 'Female' ? 12 : 13;
                      const hi = gender === 'Female' ? 15 : 17;
                      return num < lo || num > hi;             // Normal varies by gender
                  }
                  default:
                      return false;
              }
          };
        
                // Helper function to build out of range badge
        const buildOutOfRangeBadge = () => 
            `<div style="
                float:right;
                display:inline-flex;
                align-items:center;
                gap:4px;
                background:#FFF3E0;
                font-size:8px;
                font-weight:bold;
                padding:2px 4px;
                border-radius:3px;
            ">⚠️</div>`;
        

        // ----------- PAGE 1: PATIENT DETAILS WITH LOGO AND QR CODE -----------
        const photoBase64 = `data:image/webp;base64,${p.photo_base64}`;
        const qrCodeBase64 = await generateQRCodeBase64(p);

        function toHindi(text) {
            if (!text) return "N/A";
        
            // Step 1: convert to lowercase
            const lowerText = text.toLowerCase();
        
            // Step 2: convert to Hindi (Devanagari)
            return Sanscript.t(lowerText, "itrans", "telugu");
        }

        const page1 = document.createElement("div");
        page1.style.pageBreakAfter = "always";
page1.innerHTML = `
  <div style="
    width: 794px; 
    height: 900px; 
    padding: 30px; 
    font-family: Arial, sans-serif; 
    box-sizing: border-box; 
    position: relative;
    border: 0px solid #000;
    margin: auto;
  ">

    <!-- Header -->
    <div style="display: flex; align-items: center; margin-bottom: 15px;">
      <img src="./campanylogo.jpeg" alt="Company Logo" style="height: 80px;">
      <h1 style="margin-left: 20px; font-size: 28px;"></h1>
    </div>

    <!-- Photo LEFT + QR RIGHT (side by side) -->
    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px;">
      
      <!-- Patient Photo on LEFT -->
      <div style="width: 220px; height: 180px; border-radius: 50%; overflow: hidden; border: 4px solid #4285F4;">
        <img src="${photoBase64}" alt="Profile Photo" style="width: 100%; height: 100%; object-fit: cover;">
      </div>

      <!-- QR Code on RIGHT -->
      <div style="text-align: center;">
        <p style="margin-bottom: 10px; font-weight: bold;">Scan for Details</p>
        <img src="${qrCodeBase64}" alt="QR Code" style="width: 160px; height: 160px; border: 1px solid #000;">
      </div>

    </div>

    <!-- Patient Details Table BELOW -->
    <table style="border-collapse: collapse; font-size: 16px; width: 100%;">
      <tr>
        <td style="padding: 10px; border: 1px solid #000; background-color: #f0f0f0; font-weight: bold; width: 40%;">Unique Patient ID / ప్రత్యేక గుర్తింపు సంఖ్య</td>
        <td style="padding: 10px; border: 1px solid #000;">${p.unique_patient_id || 'N/A'}</td>
      </tr>
      <tr>
        <td style="padding: 10px; border: 1px solid #000; background-color: #f0f0f0; font-weight: bold;">Name / పేరు</td>
        <td style="padding: 10px; border: 1px solid #000;">${p.name || 'N/A'} / ${toHindi(p.name)}</td>
      </tr>
      <tr>
        <td style="padding: 10px; border: 1px solid #000; background-color: #f0f0f0; font-weight: bold;">Age / వయస్సు</td>
        <td style="padding: 10px; border: 1px solid #000;">${p.age || 'N/A'} year/వర్ష </td>
      </tr>
      <tr>
        <td style="padding: 10px; border: 1px solid #000; background-color: #f0f0f0; font-weight: bold;">Gender / లింగం</td>
        <td style="padding: 10px; border: 1px solid #000;">${p.gender || 'N/A'} / ${getGenderInEnglish(p.gender)}</td>
      </tr>
      <tr>
        <td style="padding: 10px; border: 1px solid #000; background-color: #f0f0f0; font-weight: bold;">Phone / ఫోన్</td>
        <td style="padding: 10px; border: 1px solid #000;">${p.phone || 'N/A'}</td>
      </tr>
      <tr>
        <td style="padding: 10px; border: 1px solid #000; background-color: #f0f0f0; font-weight: bold;">Loan Number / రుణ సంఖ్య</td>
        <td style="padding: 10px; border: 1px solid #000;">${p.loan_number || 'N/A'}</td>
      </tr>
      <tr>
        <td style="padding: 10px; border: 1px solid #000; background-color: #f0f0f0; font-weight: bold;">Aadhar Number / ఆధార్ సంఖ్య</td>
        <td style="padding: 10px; border: 1px solid #000;">${p.aadhar_number || 'N/A'}</td>
      </tr>
    </table>


    
    
    <!-- Health Instructions Section -->
<div style="margin-top: 8px; font-family: Arial, sans-serif;">
  
  <!-- Header -->
  <div style="background: linear-gradient(90deg,#0d1b2a 0%,#1565C0 100%); padding: 9px 16px; border-radius: 4px 4px 0 0; display: flex; align-items: center; gap: 10px;">
    <span style="font-size: 13px; font-weight: bold; color: #fff; letter-spacing: 0.4px;">Health Advisory </span>
    <span style="font-size: 11px; color: #b0c8e8;">/ స్వాస్థ్య నిర్దేశం</span>
  </div>

  <!-- Grid -->
  <div style="display: grid; grid-template-columns: 1fr 1fr; border: 1px solid #dde3ea; border-top: none; border-radius: 0 0 4px 4px; overflow: hidden;">

    <!-- 1 -->
    <div style="padding: 10px 14px; border-right: 1px solid #dde3ea; border-bottom: 1px solid #dde3ea; display: flex; gap: 10px;">
      <div style="width:32px;height:32px;border-radius:50%;background:#eef3fb;display:flex;align-items:center;justify-content:center;">🥗</div>
      <div>
        <div style="font-size:11.5px;font-weight:bold;">Eat a Balanced Diet</div>
        <div style="font-size:10px;">Eat a balanced diet rich in fruits and vegetables.</div>
        <div style="font-size:10px;font-style:italic;">పండ్లు మరియు కూరగాయలు అధికంగా ఉండే సమతుల ఆహారం తీసుకోండి.</div>
      </div>
    </div>

    <!-- 2 -->
    <div style="padding: 10px 14px; border-bottom: 1px solid #dde3ea; display: flex; gap: 10px;">
      <div style="width:32px;height:32px;border-radius:50%;background:#e6f9f2;display:flex;align-items:center;justify-content:center;">🏃</div>
      <div>
        <div style="font-size:11.5px;font-weight:bold;">Exercise Regularly</div>
        <div style="font-size:10px;">At least 30 minutes of physical activity daily.</div>
        <div style="font-size:10px;font-style:italic;">దరరోజు కనీసం 30 నిమిషాల శారీరక పని చేయండి.</div>
      </div>
    </div>

    <!-- 3 -->
    <div style="padding: 10px 14px; border-right: 1px solid #dde3ea; border-bottom: 1px solid #dde3ea; display: flex; gap: 10px;">
      <div style="width:32px;height:32px;border-radius:50%;background:#fff8ec;display:flex;align-items:center;justify-content:center;">💧</div>
      <div>
        <div style="font-size:11.5px;font-weight:bold;">Stay Hydrated</div>
        <div style="font-size:10px;">Drink plenty of water throughout the day.</div>
        <div style="font-size:10px;font-style:italic;">రోజంతా తగినంత నీరు తాగండి.</div>
      </div>
    </div>

    <!-- 4 -->
    <div style="padding: 10px 14px; border-bottom: 1px solid #dde3ea; display: flex; gap: 10px;">
      <div style="width:32px;height:32px;border-radius:50%;background:#e6f9f2;display:flex;align-items:center;justify-content:center;">😴</div>
      <div>
        <div style="font-size:11.5px;font-weight:bold;">Get Adequate Sleep</div>
        <div style="font-size:10px;">Ensure 7–8 hours of sleep every night.</div>
        <div style="font-size:10px;font-style:italic;">దరరోజు 7–8 గంటల సారి తప్పక ఉద్యోగం చేయండి.</div>
      </div>
    </div>

    <!-- 5 -->
    <div style="padding: 10px 14px; border-right: 1px solid #dde3ea; display: flex; gap: 10px;">
      <div style="width:32px;height:32px;border-radius:50%;background:#fff0f0;display:flex;align-items:center;justify-content:center;">🚭</div>
      <div>
        <div style="font-size:11.5px;font-weight:bold;">Healthy Lifestyle Habits</div>
        <div style="font-size:10px;">Avoid tobacco, alcohol, and all harmful substances for a healthier life.</div>
        <div style="font-size:10px;font-style:italic;">తంబాఖూ, మద్య మరియు ఇతర హానికారక పదార్థాలు నుండి దూరంగా ఉన్నది.</div>
      </div>
    </div>

    <!-- 6 -->
    <div style="padding: 10px 14px; display: flex; gap: 10px;">
      <div style="width:32px;height:32px;border-radius:50%;background:#f0f7ff;display:flex;align-items:center;justify-content:center;">🧼</div>
      <div>
        <div style="font-size:11.5px;font-weight:bold;">Maintain Personal Hygiene</div>
        <div style="font-size:10px;">Maintain good personal hygiene, including regular handwashing.</div>
        <div style="font-size:10px;font-style:italic;">సారీ వ్యక్తిగత స్వచ్ఛత జాళవో, జేమ్ కి నియమిత రీతే చెట్టు ధోవా.</div>
      </div>
    </div>

  </div>
</div>


<!-- Color Meaning Legend - Compact Single Row -->
<div style="margin-top: 6px; font-family: Arial, sans-serif;">

  <!-- Header -->
  <div style="background: linear-gradient(90deg,#0d1b2a 0%,#1565C0 100%); padding: 6px 16px; border-radius: 4px 4px 0 0; display: flex; align-items: center; gap: 10px;">
    <span style="font-size: 11px; font-weight: bold; color: #fff; letter-spacing: 0.4px;">Vital Indicator Color Guide</span>
    <span style="font-size: 11px; color: #fff;">/ప్రాణ సంకేత రంగుల మార్గదర్శిని</span>
  </div>

  <!-- Row -->
  <div style="border: 1px solid #dde3ea; border-top: none; border-radius: 0 0 4px 4px; padding: 8px 16px; background: #f9fafc; display: flex; align-items: center; gap: 20px;">

    <!-- Normal -->
    <div style="display: flex; align-items: center; gap: 7px; flex: 1;">
      <div style="width: 13px; height: 13px; border-radius: 50%; background: #00C47A;"></div>
      <div>
        <div style="font-size: 10px; font-weight: bold; color: #0B8F6A;">Normal / సాధారణ</div>
        <div style="font-size: 8.5px; color: #555;">Within healthy range</div>
      </div>
    </div>

    <div style="width: 1px; height: 32px; background: #dde3ea;"></div>

    <!-- Warning -->
    <div style="display: flex; align-items: center; gap: 7px; flex: 1;">
      <div style="width: 13px; height: 13px; border-radius: 50%; background: #FB8C00;"></div>
      <div>
        <div style="font-size: 10px; font-weight: bold; color: #E65100;">Warning / ఎందుకు</div>
        <div style="font-size: 8.5px; color: #555;">Slightly outside range</div>
      </div>
    </div>

    <div style="width: 1px; height: 32px; background: #dde3ea;"></div>

    <!-- Out of Range -->
    <div style="display: flex; align-items: center; gap: 7px; flex: 1;">
      <div style="width: 13px; height: 13px; border-radius: 50%; background: #E53935;"></div>
      <div>
        <div style="font-size: 10px; font-weight: bold; color: #B71C1C;">Out of Range / సీమా నుండి బయట</div>
        <div style="font-size: 8.5px; color: #555;">Abnormal condition</div>
      </div>
    </div>

    <div style="width: 1px; height: 32px; background: #dde3ea;"></div>

    <!-- Critical -->
    <div style="display: flex; align-items: center; gap: 7px; flex: 1;">
      <div style="
        background:#FFEBEE;
        padding:2px 5px;
        border-radius:3px;
        font-weight:bold;
        font-size:10px;
        color:#B71C1C;
      ">⚠️</div>
      <div>
        <div style="font-size: 10px; font-weight: bold; color: #B71C1C;">Critical /తీవ్రమైన</div>
        <div style="font-size: 8.5px; color: #555;">Immediate medical attention needed</div>
      </div>
    </div>

  </div>
</div>

  </div>
`;
        container.appendChild(page1);

        // ----------- PAGE 2: BMR PDF IMAGE -----------
        const imageDataUrl = await convertBase64PdfToImage(bmr_pdf_base64);

                const page2 = document.createElement("div");
        page2.style.pageBreakAfter = "always";
        
        const getBmr = (key, decimals = 2) => {
            const val = bmrData[key];
            if (val === null || val === undefined || val === '') return 'N/A';
            const num = parseFloat(val);
            return isNaN(num) ? val : num.toFixed(decimals);
        };
        
        const bmrCreatedAt = bmrData.created_at
            ? new Date(bmrData.created_at).toLocaleDateString('en-IN')
            : new Date().toLocaleDateString('en-IN');

        page2.innerHTML = `
<style>
.bmr-rc {
  width: 794px;
  background: #fff;
  font-family: Arial, sans-serif;
  color: #3D5166;
  padding: 10px;
  margin: 0 auto;
}
.bmr-inner { border: 1px solid #DDE3EA; overflow: hidden; }
.bmr-rh {
  background: #0d1b2a;
  color: white;
  padding: 11px 18px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.bmr-report-title {
  font-size: 28px;
  letter-spacing: 2px;
  text-transform: uppercase;
  font-weight: bold;
  color: white;
}
.bmr-sh {
  background: #F5F7FA;
  border-left: 5px solid #1565C0;
  padding: 6px 14px;
  margin: 5px 0;
  font-size: 9.5px;
  font-weight: bold;
  color: #1565C0;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  border-bottom: 1px solid #DDE3EA;
}
.bmr-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  border-bottom: 1px solid #DDE3EA;
}
.bmr-grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-bottom: 1px solid #DDE3EA;
}
.bmr-cell {
  padding: 11px 14px;
  border-right: 1px solid #DDE3EA;
}
.bmr-cell:last-child { border-right: none; }
.bmr-label-en { font-size: 8.5px; color: #8898AA; display: block; margin-bottom: 1px; line-height: 1.3; }
.bmr-label-hi { font-size: 10px; font-weight: bold; color: #3D5166; display: block; margin-bottom: 4px; line-height: 1.3; }
.bmr-val { font-size: 26px; font-weight: 800; color: #1a1a1a; line-height: 1.1; }
.bmr-val-accent { font-size: 26px; font-weight: 800; color: #1565C0; line-height: 1.1; }
.bmr-val-green { font-size: 26px; font-weight: 800; color: #0B8F6A; line-height: 1.1; }
.bmr-unit { font-size: 9px; color: #5a6a7a; font-weight: normal; }
.bmr-pbar { height: 5px; display: flex; border-radius: 3px; overflow: hidden; margin-top: 7px; background: #eee; }
.bmr-range { font-size: 7.5px; color: #8898AA; margin-top: 4px; line-height: 1.4; }
.bmr-highlight-box {
  background: linear-gradient(135deg, #0d1b2a 0%, #1565C0 100%);
  margin: 8px 10px;
  border-radius: 6px;
  padding: 14px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.bmr-highlight-item { text-align: center; }
.bmr-highlight-val { font-size: 32px; font-weight: 900; color: #00C47A; line-height: 1; }
.bmr-highlight-unit { font-size: 10px; color: #8ab0c8; margin-top: 2px; }
.bmr-highlight-label { font-size: 9px; color: #fff; margin-top: 4px; font-weight: bold; letter-spacing: 0.3px; }
.bmr-highlight-label-hi { font-size: 8px; color: #8ab0c8; margin-top: 2px; }
.bmr-divider { width: 1px; background: rgba(255,255,255,0.2); height: 60px; }
.bmr-footer {
  background: #0d1b2a;
  color: #7a9bb5;
  font-size: 7.5px;
  padding: 5px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>

<div class="bmr-rc">
  <div class="bmr-inner">

    <!-- HEADER — same layout as Page 3 -->
    <div class="bmr-rh">
      <div>
        <img src="./campanylogo.jpeg" alt="Company Logo" style="max-width:150px;height:35px;margin-bottom:4px;" />
        <div style="font-size:17.5px;color:white;margin-top:2px">Date: ${bmrCreatedAt}</div>
      </div>
      <div style="text-align:center">
        <div class="bmr-report-title">BMR REPORT</div>
      </div>
      <div style="text-align:right">
        <img src="./Medibuddy.jpg" alt="MediBuddy" style="max-width:120px;height:30px;margin-bottom:2px;" />
        <div style="font-size:12px;font-weight:bold;color:white;margin-top:1px">${p.name} / ${toHindi(p.name)}</div>
        <div style="font-size:9.5px;color:white;margin-top:1px">Loan no: ${p.loan_number}</div>
        <div style="font-size:9.5px;color:white">Aadhar no: ${p.aadhar_number}</div>
      </div>
    </div>

<!-- HIGHLIGHT STRIP: Height, Weight, BMR, BMI -->
    <div class="bmr-highlight-box">
      <div class="bmr-highlight-item">
        <div class="bmr-highlight-val">${getValue(vitals.height)}</div>
        <div class="bmr-highlight-unit">cm</div>
        <div class="bmr-highlight-label">Height</div>
        <div class="bmr-highlight-label-hi"> ఎత్తు</div>
      </div>
      <div class="bmr-divider"></div>
      <div class="bmr-highlight-item">
        <div class="bmr-highlight-val">${getBmr('weight', 1)}</div>
        <div class="bmr-highlight-unit">kg</div>
        <div class="bmr-highlight-label">Weight</div>
        <div class="bmr-highlight-label-hi"> బరువు</div>
      </div>
      <div class="bmr-divider"></div>
      <div class="bmr-highlight-item">
        <div class="bmr-highlight-val">${getBmr('bmr', 0)}</div>
        <div class="bmr-highlight-unit">kcal / day</div>
        <div class="bmr-highlight-label">BMR</div>
        <div class="bmr-highlight-label-hi">ప్రాథమిక మెటాబాలిక్ రేటు</div>
      </div>
      <div class="bmr-divider"></div>
      <div class="bmr-highlight-item">
        <div class="bmr-highlight-val">${getBmr('bmi', 1)}</div>
        <div class="bmr-highlight-unit">kg/m²</div>
        <div class="bmr-highlight-label">BMI</div>
        <div class="bmr-highlight-label-hi"> శరీర ద్రవ్యరాశి సూచీ</div>
      </div>
    </div>

    <!-- SECTION 1: BODY COMPOSITION  -->
    <div class="bmr-sh">BODY COMPOSITION —  శరీర నిర్మాణం</div>
    <div class="bmr-grid">
      <div class="bmr-cell">
        <span class="bmr-label-en">Weight</span>
        <span class="bmr-label-hi">బరువు</span>
        <span class="bmr-val">${getBmr('weight', 2)}</span> <span class="bmr-unit">kg</span>
        <div class="bmr-pbar"><div style="background:#1565C0;flex:3"></div><div style="background:#00C47A;flex:2"></div><div style="background:#FB8C00;flex:1"></div></div>
        <div class="bmr-range">Normal: 50–80 kg (varies by height)</div>
      </div>
      <div class="bmr-cell">
        <span class="bmr-label-en">Body Age</span>
        <span class="bmr-label-hi">శరీర వయస్సు</span>
        <span class="bmr-val-accent">${getBmr('body_age', 0)}</span><span class="bmr-unit">yrs</span>
        <div class="bmr-pbar"><div style="background:#FB8C00;flex:1"></div><div style="background:#00C47A;flex:3"></div><div style="background:#FB8C00;flex:1"></div><div style="background:#E53935;flex:0.8"></div></div>
        <div class="bmr-range">Total Body Age </div>
      </div>
      <div class="bmr-cell">
        <span class="bmr-label-en">Fat Free Weight</span>
        <span class="bmr-label-hi">కొవ్వు లేని బరువు</span>
        <span class="bmr-val-green">${getBmr('fat_free_weight', 1)}</span> <span class="bmr-unit">kg</span>
        <div class="bmr-pbar"><div style="background:#00C47A;flex:4"></div><div style="background:#FB8C00;flex:1"></div></div>
        <div class="bmr-range">Higher is better — reflects lean body mass</div>
      </div>
    </div>

    <!-- SECTION 2: FAT ANALYSIS -->
    <div class="bmr-sh">FAT ANALYSIS — కొవ్వు విశ్లేషణ</div>
    <div class="bmr-grid">
      <div class="bmr-cell">
        <span class="bmr-label-en">Body Fat %</span>
        <span class="bmr-label-hi"> శరీర కొవ్వు %</span>
        <span class="bmr-val">${getBmr('body_fat', 1)}</span> <span class="bmr-unit">%</span>
        <div class="bmr-pbar"><div style="background:#00C47A;flex:2.5"></div><div style="background:#FB8C00;flex:1.5"></div><div style="background:#E53935;flex:1"></div></div>
        <div class="bmr-range">Men: Normal 10–20% | Women: Normal 18–28%</div>
      </div>
      <div class="bmr-cell">
        <span class="bmr-label-en">Fat Mass</span>
        <span class="bmr-label-hi">కొవ్వు పరిమాణం</span>
        <span class="bmr-val">${getBmr('fat_mass', 1)}</span> <span class="bmr-unit">kg</span>
        <div class="bmr-pbar"><div style="background:#00C47A;flex:2"></div><div style="background:#FB8C00;flex:1.5"></div><div style="background:#E53935;flex:1"></div></div>
        <div class="bmr-range">Total fat weight in body</div>
      </div>
      <div class="bmr-cell">
        <span class="bmr-label-en">Subcutaneous Fat</span>
        <span class="bmr-label-hi">చర్మం కింద కొవ్వు</span>
        <span class="bmr-val">${getBmr('subcutaneous_fat', 2)}</span> <span class="bmr-unit">%</span>
        <div class="bmr-pbar"><div style="background:#00C47A;flex:2.5"></div><div style="background:#FB8C00;flex:1.5"></div><div style="background:#E53935;flex:1"></div></div>
        <div class="bmr-range">Normal: Men &lt;20% | Women &lt;30%</div>
      </div>
    </div>
    <div class="bmr-grid-2">
      <div class="bmr-cell" style="border-right:1px solid #DDE3EA">
        <span class="bmr-label-en">Visceral Fat</span>
        <span class="bmr-label-hi">అంతర్గత కొవ్వు</span>
        <span class="bmr-val">${getBmr('visceral_fat', 1)}</span>
        <div class="bmr-pbar"><div style="background:#00C47A;flex:3"></div><div style="background:#FB8C00;flex:1.5"></div><div style="background:#E53935;flex:1"></div></div>
        <div class="bmr-range">Normal: 1–9 | High: 10–14 | Very High: ≥15</div>
      </div>
      <div class="bmr-cell">
        <span class="bmr-label-en">Protein Mass</span>
        <span class="bmr-label-hi"> ప్రోటీన్ పరిమాణం</span>
        <span class="bmr-val-green">${getBmr('protein_mass', 1)}</span> <span class="bmr-unit">kg</span>
        <div class="bmr-pbar"><div style="background:#00C47A;flex:3.5"></div><div style="background:#FB8C00;flex:1"></div></div>
        <div class="bmr-range">Normal: ~16% of body weight</div>
      </div>
    </div>

    <!-- SECTION 3: MUSCLE & BONE -->
    <div class="bmr-sh">MUSCLE & BONE — కండరాలు మరియు ఎముకలు</div>
    <div class="bmr-grid">
      <div class="bmr-cell">
        <span class="bmr-label-en">Muscle Mass</span>
        <span class="bmr-label-hi">కండరాల పరిమాణం</span>
        <span class="bmr-val-green">${getBmr('muscle_mass', 1)}</span> <span class="bmr-unit">kg</span>
        <div class="bmr-pbar"><div style="background:#00C47A;flex:4"></div><div style="background:#FB8C00;flex:1"></div></div>
        <div class="bmr-range">Men: Normal 35–54 kg | Women: 22–36 kg</div>
      </div>
      <div class="bmr-cell">
        <span class="bmr-label-en">Muscle Rate</span>
        <span class="bmr-label-hi">కండరాల రేటు</span>
        <span class="bmr-val-green">${getBmr('muscle_rate', 1)}</span> <span class="bmr-unit">%</span>
        <div class="bmr-pbar"><div style="background:#00C47A;flex:4"></div><div style="background:#FB8C00;flex:1"></div></div>
        <div class="bmr-range">Normal: Men ≥75% | Women ≥72%</div>
      </div>
      <div class="bmr-cell">
        <span class="bmr-label-en">Skeletal Muscle</span>
        <span class="bmr-label-hi"> ఎముకల కండరాలు</span>
        <span class="bmr-val-green">${getBmr('skeletal_muscle', 1)}</span> <span class="bmr-unit">kg</span>
        <div class="bmr-pbar"><div style="background:#00C47A;flex:3.5"></div><div style="background:#FB8C00;flex:1.5"></div></div>
        <div class="bmr-range">Men: Normal 26–40 kg | Women: 18–30 kg</div>
      </div>
    </div>
    <div class="bmr-grid-2">
      <div class="bmr-cell" style="border-right:1px solid #DDE3EA">
        <span class="bmr-label-en">Bone Mass</span>
        <span class="bmr-label-hi"> ఎముకల పరిమాణం</span>
        <span class="bmr-val-accent">${getBmr('bone_mass', 1)}</span> <span class="bmr-unit">kg</span>
        <div class="bmr-pbar"><div style="background:#1565C0;flex:4"></div><div style="background:#FB8C00;flex:0.8"></div></div>
        <div class="bmr-range">Normal: Men ~3 kg | Women ~2.4 kg</div>
      </div>
      <div class="bmr-cell">
        <span class="bmr-label-en">Protein %</span>
        <span class="bmr-label-hi">ప్రోటీన్ %</span>
        <span class="bmr-val-green">${getBmr('protein', 1)}</span> <span class="bmr-unit">%</span>
        <div class="bmr-pbar"><div style="background:#00C47A;flex:3.5"></div><div style="background:#FB8C00;flex:1.2"></div></div>
        <div class="bmr-range">Normal: ≥16% of body weight</div>
      </div>
    </div>

    <!-- SECTION 4: WATER -->
    <div class="bmr-sh">BODY WATER — శరీరంలోని నీరు %</div>
    <div class="bmr-grid">
      <div class="bmr-cell">
        <span class="bmr-label-en">Water Weight</span>
        <span class="bmr-label-hi">నీటి బరువు</span>
        <span class="bmr-val-accent">${getBmr('water_weight', 1)}</span> <span class="bmr-unit">kg</span>
        <div class="bmr-pbar"><div style="background:#1565C0;flex:4"></div><div style="background:#FB8C00;flex:0.8"></div></div>
        <div class="bmr-range">Total water in body</div>
      </div>
      <div class="bmr-cell">
        <span class="bmr-label-en">Body Water %</span>
        <span class="bmr-label-hi"> శరీరంలోని నీరు %</span>
        <span class="bmr-val-accent">${getBmr('body_water', 1)}</span> <span class="bmr-unit">%</span>
        <div class="bmr-pbar"><div style="background:#1565C0;flex:4"></div><div style="background:#FB8C00;flex:1"></div><div style="background:#E53935;flex:0.5"></div></div>
        <div class="bmr-range">Normal: Men 55–65% | Women 45–60%</div>
      </div>
      <div class="bmr-cell" style="background:#f8fff9">
        <span class="bmr-label-en">Patient Unique ID</span>
        <span class="bmr-label-hi"> రోగి ప్రత్యేక గుర్తింపు సంఖ్య</span>
        <span style="font-size:20px;font-weight:800;color:#1565C0">${bmrData.patient_unique_id || p.unique_patient_id || 'N/A'}</span>
        <div style="font-size:7.5px;color:#8898AA;margin-top:8px">Report Generated: ${bmrCreatedAt}</div>
      </div>
    </div>

    <!-- FOOTER -->
    <div class="bmr-footer">
      <span>XRAi digital ® - MediBuddy </span>
      <span>Loan ID: ${p.loan_number} | BMR Report | Page 2 / 3</span>
    </div>

  </div>
</div>
`;
container.appendChild(page2);

        // ----------- PAGE 3: HEALTH CHECK-UP REPORT (PATHOLOGY) -----------
        const page3 = document.createElement("div");
        page3.style.cssText = "";

        const bp = formatBP(vitals.bp);

        const tempStatus  = getVitalStatus('temperature',   vitals.body_temperature);
        const pulseStatus = getVitalStatus('pulse',          vitals.heart_rate);
        const spo2Status  = getVitalStatus('spo2',           vitals.oxygen_saturation);
        const bpSysStatus = getVitalStatus('bp_systolic',    bp.systolic);
        const bpDiaStatus = getVitalStatus('bp_diastolic',   bp.diastolic);
        const glucStatus  = getVitalStatus('glucose',        vitals.blood_glucose);
        const hgbStatus   = getVitalStatus('hemoglobin',     vitals.hemoglobin, p.gender);

        const BP_PRIORITY = { '#E53935': 3, '#FB8C00': 2, '#00C47A': 1 };
        const bpColor = (BP_PRIORITY[bpSysStatus.color] >= BP_PRIORITY[bpDiaStatus.color])
            ? bpSysStatus.color : bpDiaStatus.color;
        const bpBar = bpColor === '#E53935'
            ? `<div style="background:#E53935;flex:5"></div>`
            : bpColor === '#FB8C00'
            ? `<div style="background:#E53935;flex:0.8"></div><div style="background:#FB8C00;flex:3.5"></div><div style="background:#E53935;flex:0.5"></div>`
            : `<div style="background:#E53935;flex:0.8"></div><div style="background:#FB8C00;flex:1.2"></div><div style="background:#00C47A;flex:2.5"></div><div style="background:#FB8C00;flex:0.8"></div><div style="background:#E53935;flex:0.5"></div>`;

        page3.innerHTML = `
<style>
* { box-sizing: border-box; margin: 0; padding: 0; }
.rc {
  width: 794px;
  background: #fff;
  font-family: Arial, sans-serif;
  color: #3D5166;
  padding: 10px;
  margin: 0 auto;
}
.inner {
  border: 1px solid #DDE3EA;
  overflow: hidden;
}
.rh {
  background: #0d1b2a;
  color: white;
  padding: 11px 18px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.brand-name { font-size: 15px; font-weight: bold; color: white; letter-spacing: 0.3px; }
.brand-tag { font-size: 7.5px; text-transform: uppercase; color: #7a9bb5; letter-spacing: 1px; margin-top: 2px; }
.brand-date { font-size: 17.5px; margin-top: 4px; color: white; }
.report-title { font-size: 22px; letter-spacing: 2.5px; text-transform: uppercase; font-weight: bold; color: white; }
.logo-bars { display: flex; gap: 3px; justify-content: flex-end; align-items: flex-end; margin-bottom: 5px; }
.bar-icon { width: 5px; border-radius: 1px; }

.sh {
  background: #F5F7FA;
  border-left: 5px solid #00C47A;
  padding: 6px 14px;
  margin: 5px 0;
  font-size: 9.5px;
  font-weight: bold;
  color: #0b8f6a;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  border-bottom: 1px solid #DDE3EA;
}

.vitals-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; border-bottom: 1px solid #DDE3EA; }
.vitals-row2 { display: grid; grid-template-columns: 1fr 1fr; border-bottom: 1px solid #DDE3EA; }
.vc { padding: 11px 14px; border-right: 1px solid #DDE3EA; }
.vc:last-child { border-right: none; }
.lhi { font-size: 10.5px; font-weight: bold; color: #3D5166; display: block; line-height: 1.3; }
.len { font-size: 8.5px; color: #8898AA; margin-bottom: 4px; display: block; line-height: 1.3; }
.vnum { font-size: 26px; font-weight: 800; color: #1a1a1a; line-height: 1.1; }
.vnum-ok { font-size: 26px; font-weight: 800; color: #0B8F6A; line-height: 1.1; }
.vunit { font-size: 9px; color: #5a6a7a; font-weight: normal; }
.range-text { font-size: 7.5px; color: #8898AA; margin-top: 4px; line-height: 1.4; }
.normal-badge { font-size: 7.5px; color: #0B8F6A; font-weight: bold; }
.warn-badge { font-size: 7.5px; color: #FB8C00; font-weight: bold; }

.pbar { height: 5px; display: flex; border-radius: 3px; overflow: hidden; margin-top: 7px; background: #eee; }

.blood-section {
  display: flex;
  padding: 14px 16px;
  align-items: flex-start;
  border-bottom: 1px solid #DDE3EA;
  background: #fff;
}
.blood-box {
  background: #FFF5F5;
  border: 1px solid #f5c5c5;
  width: 78px;
  height: 78px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-right: 22px;
  flex-shrink: 0;
  border-radius: 2px;
}
.bg-lbl { font-size: 7.5px; color: #E53935; font-weight: bold; text-align: center; line-height: 1.4; }
.bg-val { font-size: 28px; color: #E53935; font-weight: 900; line-height: 1.1; }

.ecg-section {
  padding: 12px 16px;
  border-bottom: 1px solid #DDE3EA;
  display: flex;
  align-items: center;
  gap: 18px;
  background: #f8fff9;
}

.anth-grid { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; border-bottom: 1px solid #DDE3EA; }

.vision-grid { display: grid; grid-template-columns: 1fr 1fr; border-bottom: 1px solid #DDE3EA; }
.vision-cell { padding: 10px 14px; border-right: 1px solid #DDE3EA; }
.vision-cell:last-child { border-right: none; }
.vision-val { font-size: 28px; font-weight: 800; color: #1a1a1a; margin: 4px 0 7px; }
.vi-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 8.5px;
  padding: 3px 0;
  border-bottom: 1px solid #f2f2f2;
  color: #3D5166;
}
.vi-row:last-child { border-bottom: none; }
.badge-ok {
  font-size: 7.5px;
  color: #0B8F6A;
  font-weight: bold;
  background: #E6F9F2;
  padding: 2px 9px;
  border-radius: 10px;
  white-space: nowrap;
}

.viral-grid { display: grid; grid-template-columns: 1fr 1fr; border-bottom: 1px solid #DDE3EA; }
.viral-cell {
  padding: 11px 14px;
  border-right: 1px solid #DDE3EA;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.viral-cell:last-child { border-right: none; }
.badge-neg {
  font-size: 8.5px;
  color: white;
  font-weight: bold;
  background: #00C47A;
  padding: 4px 14px;
  border-radius: 12px;
  white-space: nowrap;
  letter-spacing: 0.3px;
}

.footer {
  background: #0d1b2a;
  color: #7a9bb5;
  font-size: 7.5px;
  padding: 5px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>

<div class="rc">
  <div class="inner">

    <!-- HEADER with 30-40-30 Width Distribution -->
    <div class="rh">
      <!-- Left Section: XRAi digital + Date (30%) -->
      <div class="header-left">
        <img src="./campanylogo.jpeg" alt="Company Logo" style="max-width: 150px; height: 35px; margin-bottom: 4px;" />
        <div class="brand-date">Date: ${new Date().toLocaleDateString('en-IN')}</div>
      </div>
      
      <!-- Center Section: Report Title (40%) -->
      <div class="header-center">
        <div class="report-title">HEALTH CHECK-UP REPORT</div>
      </div>
      
      <!-- Right Section: MediBuddy + User Details (30%) -->
      <div class="header-right">
        <img src="./Medibuddy.jpg" alt="MediBuddy" style="max-width: 120px; height: 30px; margin-bottom: 2px;" />
        <div style="font-size:12.5px;font-weight:bold;color:white;margin-top:1px">${p.name}/ ${toHindi(p.name)}</div>
        <div style="font-size:10px;color:white;margin-top:1px">Loan no/ రుణ సంఖ్య: ${p.loan_number}</div>
        <div style="font-size:10px;color:white">Aadhar no/ ఆధార్ సంఖ్య: ${p.aadhar_number}</div>
      </div>
    </div>

    <!-- VITALS -->
    <div class="sh">VITALS -ముఖ్య సంకేతాలు</div>

    <!-- Row 1: Temperature, Pulse, Oxygen -->
    <div class="vitals-grid">
      <div class="vc">
        <span class="len">Body Temperature</span>
        <span class="lhi"> శరీర ఉష్ణోగ్రత</span>
        <div style="margin-top:2px"><span class="vnum" style="color:${tempStatus.color}">${getValue(vitals.body_temperature)}</span> <span class="vunit">°F</span>${isOutOfRange('temperature', vitals.body_temperature) ? buildOutOfRangeBadge() : ''}</div>
        <div class="pbar">${tempStatus.bar}</div>
        <div class="range-text">
          <div>Range</div>
          <div>Normal: 97-100 °F</div>
        </div>
      </div>
      <div class="vc">
        <span class="len">Pulse Rate</span>
        <span class="lhi">నాడి</span>
        <div style="margin-top:2px"><span class="vnum" style="color:${pulseStatus.color}">${getValue(vitals.heart_rate)}</span> <span class="vunit">/min</span>${isOutOfRange('pulse', vitals.heart_rate) ? buildOutOfRangeBadge() : ''}</div>
        <div class="pbar">${pulseStatus.bar}</div>
        <div class="range-text">
          <div>Range</div>
          <div>Normal: 60-100 /min</div>
        </div>
      </div>
      <div class="vc">
        <span class="len">Oxygen Saturation</span>      
        <span class="lhi">ఆక్సిజన్ సాచ్యురేషన్</span>
        <div style="margin-top:2px"><span class="vnum-ok" style="color:${spo2Status.color}">${getValue(vitals.oxygen_saturation)}</span><span class="vunit" style="color:${spo2Status.color}">%</span>${isOutOfRange('spo2', vitals.oxygen_saturation) ? buildOutOfRangeBadge() : ''}</div>
        <div class="pbar">${spo2Status.bar}</div>
        <div class="range-text">
          <div>Range</div>
          <div>Normal: 97-100 °F</div>
        </div>
      </div>
    </div>

    <!-- Row 2: Blood Pressure, Blood Glucose -->
    <div class="vitals-row2">
      <div class="vc" style="border-right:1px solid #DDE3EA">
        <span class="len">Blood Pressure</span>
        <span class="lhi">రక్తపోటు</span>
        <div style="margin-top:2px"><span class="vnum" style="color:${bpColor}">${bp.systolic} / ${bp.diastolic}</span>${(isOutOfRange('bp_systolic', bp.systolic) || isOutOfRange('bp_diastolic', bp.diastolic))
            ? buildOutOfRangeBadge() : ''}</div>
        <div class="pbar">${bpBar}</div>
        <div class="range-text">
          <div>Range</div>
          <div>Normal: 90-120 / 60-80</div>
        </div>
      </div>
      <div class="vc">
        <span class="len">Blood Glucose (Random)</span>      
        <span class="lhi">రక్తంలో చక్కెర</span>
        <div style="margin-top:2px"><span class="vnum" style="color:${glucStatus.color}">${getValue(vitals.blood_glucose)}</span> <span class="vunit">mg/dL</span>${isOutOfRange('blood_glucose', vitals.blood_glucose) ? buildOutOfRangeBadge() : ''}</div>
        <div class="pbar">${glucStatus.bar}</div>
        <div class="range-text">
          <div>Range</div>
          <div>Normal: 70-99 | Prediabetes: 100-125 | Diabetes: ≥126</div>
        </div>
      </div>
    </div>

    <!-- BLOOD -->
    <div class="sh">BLOOD  -రక్తం </div>
    <div class="blood-section">
      <div class="blood-box">
        <div class="bg-lbl">BLOOD GROUP<br>రక్త గ్రూప్</div>
        <div class="bg-val">${getValue(vitals.blood_grouping)}</div>
      </div>
      <div style="flex:1">
        <span style="font-size:10.5px;color:#8898AA;display:block;margin-bottom:4px;line-height:1.3">Hemoglobin</span>      
        <span style="font-size:8.5px;font-weight:bold;color:#3D5166;display:block;line-height:1.3">హీమోగ్లోబిన్</span>
        <div style="margin-bottom:1px"><span class="vnum" style="color:${hgbStatus.color}">${getValue(vitals.hemoglobin)}</span> <span class="vunit">g/dL</span>${isOutOfRange('hemoglobin', vitals.hemoglobin) ? buildOutOfRangeBadge() : ''}</div>
        <div class="pbar" style="max-width:420px">${hgbStatus.bar}</div>
        <div class="range-text">
          <div>Range</div>
          <div>Low <13 - Normal 13-17 g/dL - High >17</div>
        </div>
      </div>
    </div>

    <!-- ECG -->
    <div class="sh">ECG  -ఎలక్ట్రోకార్డియోగ్రామ్</div>
    <div class="ecg-section">
      <svg width="170" height="48" viewBox="0 0 170 48" xmlns="http://www.w3.org/2000/svg">
        <polyline
          points="0,28 12,28 17,28 22,7 27,44 32,28 42,28 50,18 55,28 65,28 85,28 90,7 95,44 100,28 110,28 115,18 120,28 130,28 148,28 153,7 158,44 163,28 170,28"
          fill="none" stroke="#00C47A" stroke-width="1.8" stroke-linejoin="round"/>
      </svg>
      <div>
      <div style="font-size:12.5px;font-weight:bold;color:#0B8F6A;line-height:1.3">
            ${vitals.ecg_3_lead_1_channel 
              ? (vitals.ecg_3_lead_1_channel === 'Normal' 
                  ? 'Normal Sinus Rhythm' 
                  : 'Abnormal ECG') 
              : 'N/A'}
          </div>
          
          <div style="font-size:9.5px;color:#3D5166;margin-top:4px">
            ${vitals.ecg_3_lead_1_channel 
              ? (vitals.ecg_3_lead_1_channel === 'Normal' 
                  ? 'No abnormality detected' 
                  : 'Consult a doctor') 
              : ''}
          </div>
          
          <div style="font-size:8.5px;color:#8898AA;margin-top:2px">
            ECG — ${vitals.ecg_3_lead_1_channel === 'Normal' ? 'Normal' : 'Abnormal'}
          </div>
      </div>
    </div>

    <!-- ANTHROPOMETRIC -->
    <div class="sh">ANTHROPOMETRIC MEASUREMENTS  -మానవమితీయ కొలతలు</div>
    <div class="anth-grid">
      <div class="vc" style="border-right:1px solid #DDE3EA">
        <span class="len" style="font-size:8px">Waist Circumferenc<br>● నడుము చుట్టుకొలత</span>
        <div style="margin-top:4px"><span style="font-size:24px;font-weight:800;color:#1a1a1a">${getValue(vitals.waist_circumference)}</span><span class="vunit" style="font-size:10px"> cm</span></div>
      </div>
      <div class="vc" style="border-right:1px solid #DDE3EA">
        <span class="len" style="font-size:8px">Hip Circumference<br>● నితంబ చుట్టుకొలత</span>
        <div style="margin-top:4px"><span style="font-size:24px;font-weight:800;color:#1a1a1a">${getValue(vitals.hip_circumference)}</span><span class="vunit" style="font-size:10px"> cm</span></div>
      </div>
      <div class="vc" style="border-right:1px solid #DDE3EA">
        <span class="len" style="font-size:8px">Mid Arm Circumference<br>●  మధ్య భుజం చుట్టుకొలత</span>
        <div style="margin-top:4px"><span style="font-size:24px;font-weight:800;color:#1a1a1a">${getValue(vitals.mid_arm_circumference)}</span><span class="vunit" style="font-size:10px"> cm</span></div>
      </div>
      <div class="vc">
        <span class="len" style="font-size:8px">Waist / Hip Ratio<br>●  నడుము/నితంబ నిష్పత్తి</span>
        <div style="margin-top:4px"><span style="font-size:24px;font-weight:800;color:#1a1a1a">${getDecimalValue(vitals.waist_to_hip_ratio, 3)}</span></div>
      </div>
    </div>

    <!-- VISION -->
    <div class="sh">VISON  -చూపు</div>
    <div class="vision-grid">
      <div class="vision-cell">
        <span class="len">LEFT EYE -ఎడమ కన్ను</span>
        <div class="vision-val">${getValue(vitals.vision_left, '6 / 6')}</div>
        <div class="vi-row"><span>Color Blindness(రంగు అంధత్వం)</span><span class="badge-ok">${vitals.color_blindness ? vitals.color_blindness : 'N/A'}</span></div>
        <div class="vi-row"><span>Duochrome(డ్యుయోక్రోమ్ పరీక్ష)</span><span class="badge-ok">${vitals.ducochrome ? vitals.ducochrome  : 'N/A'}</span></div>
        <div class="vi-row"><span>Astigmatism(అస్టిగ్మాటిజం)</span><span class="badge-ok">${vitals.astigmatism ? vitals.astigmatism : 'N/A'}</span></div>
      </div>
      <div class="vision-cell">
        <span class="len">RIGHT EYE - కుడి కన్ను</span>
        <div class="vision-val">${getValue(vitals.vision_right, '6 / 6')}</div>
        <div class="vi-row"><span>Color Blindness(రంగు అంధత్వం)</span><span class="badge-ok">${vitals.color_blindness ? vitals.color_blindness : 'N/A'}</span></div>
        <div class="vi-row"><span>Duochrome(డ్యుయోక్రోమ్ పరీక్ష )</span><span class="badge-ok">${vitals.ducochrome ? vitals.ducochrome  : 'N/A'}</span></div>
        <div class="vi-row"><span>Astigmatism(అస్టిగ్మాటిజం)</span><span class="badge-ok">${vitals.astigmatism ? vitals.astigmatism : 'N/A'}</span></div>
      </div>
    </div>

    <!-- VIRAL MARKERS -->
    <div class="sh">VIRAL MARKERS -వైరల్ మార్కర్</div>
    <div class="viral-grid">
      <div class="viral-cell">
        <div>
          <div style="font-size:11px;font-weight:bold;color:#3D5166">HCV</div>
          <div style="font-size:8px;color:#8898AA;margin-top:1px">హెపటైటిస్ C వైరస్</div>
        </div>
        <span class="badge-neg">${vitals.hcv ? vitals.hcv : 'N/A'}</span>
      </div>
      <div class="viral-cell">
        <div>
          <div style="font-size:11px;font-weight:bold;color:#3D5166">HBsAg</div>
          <div style="font-size:8px;color:#8898AA;margin-top:1px">హెపటైటిస్ B ఉపరితల యాంటిజెన్</div>
        </div>
        <span class="badge-neg">${vitals.hbsag ? vitals.hbsag : 'N/A'}</span>
      </div>
    </div>

  </div><!-- /inner -->
</div>
`;
        container.appendChild(page3);

        // 3. Generate PDF Blob from 3 pages
        const pdfBlob = await html2pdf()
            .from(container)
            .set({
                margin: 0,
                filename: `Smart_Report_${p.unique_patient_id}.pdf`,
                image: { type: "jpeg", quality: 1 },
                html2canvas: { scale: 2, useCORS: true },
                jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
                pagebreak: { mode: ['avoid-all', 'css', 'legacy'] } 
            })
            .outputPdf("blob");

        // 4. Upload generated report to backend
        const formData = new FormData();
        formData.append("patient_id", p.unique_patient_id);
        const file = new File([pdfBlob], `Smart_Report_${p.unique_patient_id}.pdf`, { type: "application/pdf" });
        formData.append("report_pdf", file);

        await axios.post(`${baseUrl}/api/technician/smart-report-upload/`, formData);

        console.log("Smart report PDF generated and uploaded successfully!");

    } catch (err) {
        console.error("❌ Failed to generate/upload smart report");
        console.error("Error Details:", {
            message: err.message,
            status: err.response?.status,
            statusText: err.response?.statusText,
            data: err.response?.data,
            url: err.config?.url,
            method: err.config?.method
        });

        const errorMessage = err.response?.data?.error ||
            err.response?.data?.detail ||
            err.message ||
            'Failed to generate report. Please check if the API server is running.';

        alert(`❌ Error: ${errorMessage}\n\nMake sure:\n1. Backend server is running\n2. Patient data is complete\n3. You are connected to the internet`);
        throw err;
    }
};
